import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button as UIButton } from "@/components/ui/button";
import {
  MapPin,
  Users,
  Smartphone,
  Download,
  RefreshCw,
  BarChart3,
  TrendingUp,
  Apple,
  Monitor,
} from "lucide-react";

interface LocationData {
  latitude: number;
  longitude: number;
  city?: string;
  region?: string;
  country?: string;
  timestamp: number;
  userAgent: string;
  deviceType: "ios" | "android" | "desktop";
}

interface CityStats {
  city: string;
  region: string;
  count: number;
  devices: {
    ios: number;
    android: number;
    desktop: number;
  };
  lastVisit: number;
}

export default function LocationAnalytics() {
  const [locationData, setLocationData] = useState<LocationData[]>([]);
  const [cityStats, setCityStats] = useState<CityStats[]>([]);
  const [totalVisits, setTotalVisits] = useState(0);
  const [loading, setLoading] = useState(true);

  // Load data from localStorage and process it
  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    setLoading(true);
    try {
      const data = JSON.parse(
        localStorage.getItem("tigola_location_data") || "[]",
      ) as LocationData[];
      setLocationData(data);
      setTotalVisits(data.length);

      // Process data by city
      const cityMap = new Map<string, CityStats>();

      data.forEach((item) => {
        const cityKey = `${item.city || "Unknown"}-${item.region || "Unknown"}`;

        if (cityMap.has(cityKey)) {
          const existing = cityMap.get(cityKey)!;
          existing.count += 1;
          existing.devices[item.deviceType] += 1;
          existing.lastVisit = Math.max(existing.lastVisit, item.timestamp);
        } else {
          cityMap.set(cityKey, {
            city: item.city || "Unknown",
            region: item.region || "Unknown",
            count: 1,
            devices: {
              ios: item.deviceType === "ios" ? 1 : 0,
              android: item.deviceType === "android" ? 1 : 0,
              desktop: item.deviceType === "desktop" ? 1 : 0,
            },
            lastVisit: item.timestamp,
          });
        }
      });

      // Sort by count (highest first)
      const sortedStats = Array.from(cityMap.values()).sort(
        (a, b) => b.count - a.count,
      );

      setCityStats(sortedStats);
    } catch (error) {
      console.error("Error loading data:", error);
    }
    setLoading(false);
  };

  const clearData = () => {
    if (confirm("Are you sure you want to clear all location data?")) {
      localStorage.removeItem("tigola_location_data");
      setLocationData([]);
      setCityStats([]);
      setTotalVisits(0);
    }
  };

  const exportData = () => {
    const dataStr = JSON.stringify(locationData, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `tigola-location-data-${new Date().toISOString().split("T")[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType) {
      case "ios":
        return <Apple className="h-4 w-4" />;
      case "android":
        return <Smartphone className="h-4 w-4" />;
      default:
        return <Monitor className="h-4 w-4" />;
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Location Analytics Dashboard
              </h1>
              <p className="text-gray-600">
                Track customer interest by location from /app page visits
              </p>
            </div>
            <div className="flex gap-3">
              <UIButton onClick={loadData} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </UIButton>
              <UIButton onClick={exportData} variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export Data
              </UIButton>
              <UIButton onClick={clearData} variant="destructive" size="sm">
                Clear Data
              </UIButton>
            </div>
          </div>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Visits
              </CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalVisits}</div>
              <p className="text-xs text-muted-foreground">App page visits</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Unique Cities
              </CardTitle>
              <MapPin className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{cityStats.length}</div>
              <p className="text-xs text-muted-foreground">
                Different locations
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Mobile Users
              </CardTitle>
              <Smartphone className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {locationData.filter((d) => d.deviceType !== "desktop").length}
              </div>
              <p className="text-xs text-muted-foreground">
                {totalVisits > 0
                  ? Math.round(
                      (locationData.filter((d) => d.deviceType !== "desktop")
                        .length /
                        totalVisits) *
                        100,
                    )
                  : 0}
                % of total
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Top City</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {cityStats[0]?.city || "N/A"}
              </div>
              <p className="text-xs text-muted-foreground">
                {cityStats[0]?.count || 0} visits
              </p>
            </CardContent>
          </Card>
        </div>

        {/* City Rankings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Customer Interest by City
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                <span className="ml-2 text-gray-600">Loading data...</span>
              </div>
            ) : cityStats.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <MapPin className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No location data available yet.</p>
                <p className="text-sm">
                  Share the /app link to start collecting data.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {cityStats.map((stat, index) => (
                  <div
                    key={`${stat.city}-${stat.region}`}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex items-center justify-center w-8 h-8 bg-primary text-primary-foreground rounded-full text-sm font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {stat.city}
                        </h3>
                        <p className="text-sm text-gray-600">{stat.region}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      {/* Device breakdown */}
                      <div className="flex items-center gap-2">
                        {stat.devices.ios > 0 && (
                          <Badge
                            variant="outline"
                            className="flex items-center gap-1"
                          >
                            <Apple className="h-3 w-3" />
                            {stat.devices.ios}
                          </Badge>
                        )}
                        {stat.devices.android > 0 && (
                          <Badge
                            variant="outline"
                            className="flex items-center gap-1"
                          >
                            <Smartphone className="h-3 w-3" />
                            {stat.devices.android}
                          </Badge>
                        )}
                        {stat.devices.desktop > 0 && (
                          <Badge
                            variant="outline"
                            className="flex items-center gap-1"
                          >
                            <Monitor className="h-3 w-3" />
                            {stat.devices.desktop}
                          </Badge>
                        )}
                      </div>

                      {/* Visit count */}
                      <div className="text-right">
                        <div className="text-lg font-bold text-gray-900">
                          {stat.count}
                        </div>
                        <div className="text-xs text-gray-500">
                          {stat.count === 1 ? "visit" : "visits"}
                        </div>
                      </div>

                      {/* Last visit */}
                      <div className="text-right min-w-24">
                        <div className="text-xs text-gray-500">
                          {formatDate(stat.lastVisit)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>How to Use This Dashboard</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-gray-600">
            <p>
              • Share the link <strong>tigola.com/app</strong> to collect
              location data from potential customers
            </p>
            <p>
              • Users will be automatically redirected to download your app
              based on their device (iOS/Android)
            </p>
            <p>
              • Location data shows you which UK cities have the most interest
              in your service
            </p>
            <p>
              • Use this data to prioritize marketing efforts and service
              expansion
            </p>
            <p>
              • Data is stored locally in browser. For production, integrate
              with a proper database using the API endpoint
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
